import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Descriptions, Table, Typography, Tag, Space, Spin, Tabs, Card, Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { CustomerQuote } from '../types.ts';
import { customerQuoteAPI } from '../services/api.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { formatProductDisplay } from '../utils/productUtils.ts';
import { getQuoteStatusColor } from '../utils/quoteStatus.ts';
import QuoteHistoryTimeline from './QuoteHistoryTimeline.tsx';

const { Text } = Typography;
const { TabPane } = Tabs;

interface CustomerQuoteDetailsModalProps {
  quote: CustomerQuote | null;
  onClose: () => void;
}

// "Visualizza" preventivo cliente: stesso schema a 4 tab del modal Dettagli Ordine
// (Informazioni Generali, Dettagli Prodotti, Informazioni Cliente, Cronologia).
const CustomerQuoteDetailsModal: React.FC<CustomerQuoteDetailsModalProps> = ({ quote, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [fullQuote, setFullQuote] = useState<CustomerQuote | null>(null);
  const [history, setHistory] = useState<any[]>([]);

  const loadQuote = useCallback(async () => {
    if (!quote) return;
    setLoading(true);
    try {
      const [data, events] = await Promise.all([
        customerQuoteAPI.getCustomerQuote(quote.id),
        customerQuoteAPI.getCustomerQuoteHistory(quote.id).catch(() => []),
      ]);
      setFullQuote(data);
      setHistory(events || []);
    } catch (error) {
      setFullQuote(quote);
    } finally {
      setLoading(false);
    }
  }, [quote]);

  useEffect(() => {
    if (quote) loadQuote();
  }, [quote, loadQuote]);

  if (!quote) return null;
  const q = fullQuote || quote;
  const details = q.quote_details || [];
  const computedTotal = details.reduce((sum, d) => sum + (d.total_price || 0), 0);

  const columns = [
    {
      title: 'Prodotto',
      key: 'product',
      width: 320,
      ellipsis: { showTitle: false },
      render: (_: any, record: any) => {
        if (!record.product) return <Text>N/A</Text>;
        const display = formatProductDisplay(record.product);
        return <Text code strong title={display}>{display}</Text>;
      },
    },
    { title: 'Qta', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'center' as const,
      render: (v: number) => <Text strong>{v}</Text> },
    { title: 'Vendita Unit.', dataIndex: 'unit_price', key: 'unit_price', width: 110, align: 'right' as const,
      render: (v: number) => <Text>€ {formatCurrency(v || 0)}</Text> },
    { title: 'Totale', dataIndex: 'total_price', key: 'total_price', width: 110, align: 'right' as const,
      render: (v: number) => <Text strong style={{ color: '#1890ff' }}>€ {formatCurrency(v || 0)}</Text> },
    { title: 'Note', dataIndex: 'notes', key: 'notes', ellipsis: true,
      render: (notes: string) => notes ? <Text type="secondary" title={notes}>{notes}</Text> : '-' },
  ];

  const fmtDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : 'Non specificata');

  return (
    <Modal
      title={
        <Space>
          <Text strong style={{ fontSize: 18 }}>Dettagli Preventivo</Text>
          <Tag color="blue" style={{ fontSize: 14 }}>{q.quote_number}</Tag>
        </Space>
      }
      open={true}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        <Tabs defaultActiveKey="1">
          <TabPane tab="Informazioni Generali" key="1">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Numero Preventivo"><strong>{q.quote_number}</strong></Descriptions.Item>
              <Descriptions.Item label="Riferimento Cliente">{q.customer_reference || 'Non specificato'}</Descriptions.Item>
              <Descriptions.Item label="Cliente">{q.customer?.company || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Email Cliente">{q.customer?.email || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Data Preventivo">{fmtDate(q.quote_date)}</Descriptions.Item>
              <Descriptions.Item label="Data Scadenza">{fmtDate(q.expiry_date)}</Descriptions.Item>
              <Descriptions.Item label="Data Consegna Richiesta">{fmtDate(q.requested_delivery_date)}</Descriptions.Item>
              <Descriptions.Item label="Stato">
                <Tag color={getQuoteStatusColor(q.quote_status)}>{q.quote_status?.display_name || 'N/A'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Ordine Stock">
                {q.reorder ? <Tag color="orange" icon={<ReloadOutlined />}>ORDINE STOCK</Tag> : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Importo Totale">
                <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  € {formatCurrency(q.total_amount != null ? q.total_amount : computedTotal)}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Destinazione Spedizione" span={2}>
                {q.shipping_destination ? (
                  <Space direction="vertical" size="small">
                    <Text strong>{q.shipping_destination.name}</Text>
                    {q.shipping_destination.address && <Text type="secondary">{q.shipping_destination.address}</Text>}
                  </Space>
                ) : 'Non specificata'}
              </Descriptions.Item>
              <Descriptions.Item label="Ordine Generato" span={2}>
                {q.converted_order ? (
                  <Space>
                    <Tag color="green">{q.converted_order.order_number}</Tag>
                    <Text type="secondary">creato dalla conversione di questo preventivo</Text>
                  </Space>
                ) : 'Nessuno (preventivo non ancora convertito)'}
              </Descriptions.Item>
              {q.notes && (
                <Descriptions.Item label="Note" span={2}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{q.notes}</div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </TabPane>

          <TabPane tab="Dettagli Prodotti" key="2">
            <Table dataSource={details} columns={columns} pagination={false} rowKey="id" size="small" />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <strong>Totale Preventivo: € {formatCurrency(q.total_amount != null ? q.total_amount : computedTotal)}</strong>
            </div>
          </TabPane>

          <TabPane tab="Informazioni Cliente" key="3">
            {q.customer ? (
              <Card size="small">
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Nome Azienda">{q.customer.company}</Descriptions.Item>
                  {q.customer.customer_code && <Descriptions.Item label="Codice Cliente">{q.customer.customer_code}</Descriptions.Item>}
                  {q.customer.contact_person && <Descriptions.Item label="Persona di Contatto">{q.customer.contact_person}</Descriptions.Item>}
                  {q.customer.email && <Descriptions.Item label="Email">{q.customer.email}</Descriptions.Item>}
                  {q.customer.phone && <Descriptions.Item label="Telefono">{q.customer.phone}</Descriptions.Item>}
                  {q.customer.address && (
                    <Descriptions.Item label="Indirizzo">
                      {q.customer.address}
                      {q.customer.city && `, ${q.customer.city}`}
                      {q.customer.postal_code && ` ${q.customer.postal_code}`}
                      {q.customer.country && `, ${q.customer.country}`}
                    </Descriptions.Item>
                  )}
                  {q.customer.vat_number && <Descriptions.Item label="Partita IVA">{q.customer.vat_number}</Descriptions.Item>}
                </Descriptions>
              </Card>
            ) : (
              <Alert message="Informazioni cliente non disponibili" type="info" />
            )}
          </TabPane>

          <TabPane tab="Cronologia" key="4">
            <QuoteHistoryTimeline events={history} />
          </TabPane>
        </Tabs>

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default CustomerQuoteDetailsModal;
